import _ from 'lodash'
import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import bulkOrderModel, { BulkOrder, UpdateBulkOrderDto } from '@/core/models/bulk-order.model'
import { SearchBulkOrdersQuery, SearchRequestOptions } from '@/core/types/request'

import { RootState } from '@/redux/rootReducer'

export const bulkOrderAdapter = createEntityAdapter<BulkOrder, BulkOrder['_id']>({ selectId: (bulkOrder) => bulkOrder._id })

const bulkOrderSelector = bulkOrderAdapter.getSelectors((state: RootState) => state.bulkOrder.bulkOrders)

export const selectBulkOrders = bulkOrderSelector.selectAll
export const selectBulkOrderById = bulkOrderSelector.selectById
export const selectBulkOrderSearchResult = (state: RootState) => state.bulkOrder.search.result
export const selectBulkOrderSearchParams = (state: RootState) => state.bulkOrder.search.params
export const selectBulkOrderUpdateRequestTracker = (state: RootState) => state.bulkOrder.requestTracker.update

const initialState = _.merge(bulkOrderModel.getInitialState(), {})

const bulkOrderSlice = createSlice({
  name: bulkOrderModel.reducerName,
  initialState,
  reducers: {
    getBulkOrder(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
    getBulkOrderSuccess(state, action: PayloadAction<BulkOrder>) {
      bulkOrderAdapter.setOne(state.bulkOrders, action.payload)
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getBulkOrderFailure(state, action) {
      state.requestTracker.get.error = action.payload
      state.requestTracker.get.loading = false
    },
    listBulkOrders: {
      reducer: (state, action: PayloadAction<{ query: SearchBulkOrdersQuery; options?: SearchRequestOptions }>) => {
        state.search.params.query = action.payload.query
        state.search.params.options = action.payload.options
        state.requestTracker.list.loading = true
      },
      prepare: (query: SearchBulkOrdersQuery, options?: SearchRequestOptions) => ({
        payload: {
          query,
          options,
        },
      }),
    },
    listBulkOrdersSuccess(state, action: PayloadAction<{ items: BulkOrder[]; totalCount: number }>) {
      const { items, totalCount } = action.payload
      bulkOrderAdapter.setAll(state.bulkOrders, items)
      state.search.result.totalCount = totalCount
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    listBulkOrdersFailure(state, action) {
      state.requestTracker.list.error = action.payload
      state.requestTracker.list.loading = false
    },
    updateBulkOrder: {
      reducer(state, action: PayloadAction<{ bulkOrderId: string; updateBulkOrderDto: UpdateBulkOrderDto }>) {
        state.requestTracker.update.loading = true
      },
      prepare: (bulkOrderId: string, updateBulkOrderDto: UpdateBulkOrderDto) => ({
        payload: { bulkOrderId, updateBulkOrderDto },
      }),
    },
    updateBulkOrderSuccess(state, action: PayloadAction<BulkOrder>) {
      bulkOrderAdapter.setOne(state.bulkOrders, action.payload)
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updateBulkOrderFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
  },
})

export const {
  getBulkOrder,
  getBulkOrderSuccess,
  getBulkOrderFailure,
  listBulkOrders,
  listBulkOrdersSuccess,
  listBulkOrdersFailure,
  updateBulkOrder,
  updateBulkOrderSuccess,
  updateBulkOrderFailure,
} = bulkOrderSlice.actions

export default bulkOrderSlice.reducer
